import React, { FC, useState } from 'react';
import { useDeleteCommentNoteEntry } from '../../../../hooks/api/Property/useDeleteCommentNoteEntry';
import ConfirmationPage from '../../../configurations/approaches/ConfirmationPage';
import { TrashIcon } from '../../../icons/OurIcons';

type NoteHistoryListProps = {
  propertyId: string;
  commentData: any;
  refetchCommentData: () => void;
}

const formatTimestamp = (isoString: string) => {
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });
};

const NoteHistoryList: FC<NoteHistoryListProps> = ({ propertyId, commentData, refetchCommentData }) => {
  const [ pendingDeleteId, setPendingDeleteId ] = useState<string | null>(null);
  const { mutateAsync: deleteNoteEntry } = useDeleteCommentNoteEntry();

  const entries = commentData?.comment?.note_entries || [];
  const commentId = commentData?.comment?.id;

  const handleDelete = async (entryId: string) => {
    if (!commentId) return;
    try {
      await deleteNoteEntry({ property_id: propertyId, comment_id: commentId, note_entry_id: entryId });
      await refetchCommentData();
    } catch (error) {
      console.error('Error deleting note:', error);
    } finally {
      setPendingDeleteId(null);
    }
  };

  if (pendingDeleteId) {
    return (
      <ConfirmationPage
        onCancel={() => setPendingDeleteId(null)}
        onConfirm={() => handleDelete(pendingDeleteId)}
        confirmationText='Confirm you want to delete this note'
        confirmationButtonText='Delete'
      />
    );
  }

  return (
    <div>
      <h3 className='text-lg font-semibold text-gray-900 mb-2'>Note History</h3>
      {entries.length === 0 ? (
        <div className='rounded-xl bg-gray-100 p-6 text-center text-gray-400 text-sm'>
          No notes yet -- add one above.
        </div>
      ) : (
        <div className='space-y-3 max-h-80 overflow-y-auto'>
          {entries.map((entry) => (
            <div key={entry.id} className='rounded-xl bg-gray-100 p-4 relative group'>
              <p className='text-sm text-gray-900 font-medium'>
                {entry.user_name} &nbsp;&bull;&nbsp; {formatTimestamp(entry.created_at)}
              </p>
              <p className='text-sm text-gray-700 mt-2 whitespace-pre-line'>{entry.body}</p>
              <button
                onClick={() => setPendingDeleteId(entry.id)}
                className='absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity'
              >
                <TrashIcon height={14} width={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NoteHistoryList;
